import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
  Image,
  Dimensions,
  ScrollView,
  Modal,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialIcons } from '@expo/vector-icons';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth, db } from '../firebaseConfig';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';
import { signOut } from 'firebase/auth';

const { width } = Dimensions.get('window');
const isLargeScreen = width > 800;

const LoginScreen = ({ navigation }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  useEffect(() => {
    navigation.setOptions({ headerShown: false });

    const checkFirstTimeUse = async () => {
      try {
        const privacyAccepted = await AsyncStorage.getItem('privacyAccepted');
        if (!privacyAccepted) {
          setShowPrivacyModal(true); // Mostra o modal se ainda não foi aceito
        } else {
          loadRememberedLogin();
        }
      } catch (error) {
        console.error('Erro ao verificar aceitação de privacidade:', error);
      }
    };

    const loadRememberedLogin = async () => {
      try {
        const savedEmail = await AsyncStorage.getItem('email');
        const savedPassword = await AsyncStorage.getItem('password');
        const savedRemember = await AsyncStorage.getItem('remember');

        if (savedRemember === 'true') {
          if (savedEmail) setEmail(savedEmail);
          if (savedPassword) setPassword(savedPassword);
          setRemember(true);
        }
      } catch (error) {
        console.error('Erro ao carregar dados salvos', error);
      }
    };

    checkFirstTimeUse();
  }, [navigation]);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Verifica se os termos foram aceitos
        const privacyAccepted = await AsyncStorage.getItem('privacyAccepted');
        if (!privacyAccepted) {
          setShowPrivacyModal(true);
          return;
        }
  
        // Verifica se o login automático deve ser realizado
        const savedRemember = await AsyncStorage.getItem('remember');
        const savedEmail = await AsyncStorage.getItem('email');
        const savedPassword = await AsyncStorage.getItem('password');
  
        if (savedRemember === 'true' && savedEmail && savedPassword) {
          await handleAutomaticLogin(savedEmail, savedPassword);
        }
      } catch (error) {
        console.error('Erro ao inicializar o aplicativo:', error);
      }
    };
  
    initializeApp();
  }, []);  
  
  const handleAutomaticLogin = async (email, password) => {
    try {
      setLoading(true);
      await signInWithEmailAndPassword(auth, email, password);
      const user = auth.currentUser;
  
      if (user) {
        const userDocRef = doc(db, 'usuarios', user.uid);
        const userDoc = await getDoc(userDocRef);
  
        if (userDoc.exists()) {
          const userData = userDoc.data();
  
          if (userData.ativo === false) {
            setLoading(false);
            Alert.alert(
              'Cadastro realizado!',
              'Seu cadastro foi feito com sucesso e está aguardando ativação. Entre em contato com o suporte para mais informações.'
            );
            return;
          }
  
          // Redireciona para a tela apropriada com base no tipo de usuário
          setLoading(false);
          if (userData.tipo === 'motoboy') {
            navigation.navigate('MotoboyHomeScreen');
          } else if (userData.tipo === 'empresa') {
            navigation.navigate('CompanyOrderScreen');
          }
        } else {
          setLoading(false);
          Alert.alert('Erro', 'Tipo de usuário não encontrado.');
        }
      }
    } catch (error) {
      setLoading(false);
      console.error('Erro ao realizar login automático:', error);
    }
  };  

  const acceptPrivacyTerms = async () => {
    try {
      await AsyncStorage.setItem('privacyAccepted', 'true');
      setShowPrivacyModal(false);
      Alert.alert('Obrigado!', 'Você aceitou os termos de privacidade.');
    } catch (error) {
      console.error('Erro ao salvar aceitação de privacidade:', error);
    }
  };
  const showAlert = (title, message) => {
    Alert.alert(title, message);
  };

  const registerForPushNotificationsAsync = async () => {
    try {
      let token;

      if (Platform.OS === 'android') {
        await Notifications.setNotificationChannelAsync('default', {
          name: 'default',
          importance: Notifications.AndroidImportance.MAX,
          vibrationPattern: [0, 250, 250, 250],
          lightColor: '#FF231F7C',
        });
      }

      const { status: existingStatus } = await Notifications.getPermissionsAsync();
      let finalStatus = existingStatus;

      if (existingStatus !== 'granted') {
        const { status } = await Notifications.requestPermissionsAsync();
        finalStatus = status;
      }

      if (finalStatus !== 'granted') {
        console.log('Permissão para notificações não foi concedida.');
        return null;
      }

      token = (await Notifications.getExpoPushTokenAsync()).data;
      console.log('Token de notificação gerado:', token);
      return token;
    } catch (error) {
      console.error('Erro ao registrar notificações push:', error);
      return null;
    }
  };

  const registerPushTokenIfNeeded = async (userDocRef, token) => {
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      const { expoPushToken } = userDoc.data();
      if (expoPushToken !== token) {
        await updateDoc(userDocRef, { expoPushToken: token });
        console.log('Token atualizado no Firestore.');
      }
    }
  };  

  const handleLogin = async () => {
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
  
      if (remember) {
        await AsyncStorage.setItem('email', email);
        await AsyncStorage.setItem('password', password);
        await AsyncStorage.setItem('remember', 'true');
      } else {
        await AsyncStorage.removeItem('email');
        await AsyncStorage.removeItem('password');
        await AsyncStorage.setItem('remember', 'false');
      }
  
      const user = auth.currentUser;
      if (user) {
        const userDocRef = doc(db, 'usuarios', user.uid);
        const userDoc = await getDoc(userDocRef);
  
        if (userDoc.exists()) {
          const userData = userDoc.data();
  
          // Verificação do campo "ativo"
          if (userData.ativo === false) {
            setLoading(false);
            Alert.alert(
              'Cadastro realizado!',
              'Seu cadastro foi feito com sucesso e está aguardando ativação. Entre em contato com o suporte para mais informações.'
            );
            return;
          }
  
          const token = await registerForPushNotificationsAsync();
          if (token) {
            await updateDoc(userDocRef, { expoPushToken: token });
            console.log('Token salvo no Firestore para o usuário:', user.uid);
          }
  
          // Redirecionar para a tela correta com base no tipo de usuário
          setLoading(false);
          if (userData.tipo === 'motoboy') {
            navigation.navigate('MotoboyHomeScreen');
          } else if (userData.tipo === 'empresa') {
            navigation.navigate('CompanyOrderScreen');
          }
        } else {
          setLoading(false);
          Alert.alert('Erro', 'Tipo de usuário não encontrado.');
        }
      }
    } catch (error) {
      setLoading(false);
      Alert.alert('Erro', 'Senha ou email incorreto.');
    }
  };
  

  const handleForgotPassword = async () => {
    if (!email) {
      Alert.alert('Erro', 'Por favor, insira seu e-mail para redefinir a senha.');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      Alert.alert('Sucesso', 'Um email para redefinição de senha foi enviado.');
    } catch (error) {
      Alert.alert('Erro', 'Não foi possível enviar o email de redefinição de senha.');
    }
  };
  
  const handleLogout = async () => {
    try {
      // Desloga no Firebase
      await signOut(auth);
  
      // Limpa os dados do AsyncStorage
      await AsyncStorage.removeItem('email');
      await AsyncStorage.removeItem('password');
      await AsyncStorage.setItem('remember', 'false');
  
      // Redireciona para a tela de login
      navigation.replace('LoginScreen');
    } catch (error) {
      console.error('Erro ao deslogar:', error);
      Alert.alert('Erro', 'Não foi possível deslogar. Tente novamente.');
    }
  };

  return (
    <>
      <LinearGradient colors={['#0f2027', '#203a43', '#2c5364']} style={styles.container}>
        {isLargeScreen ? (
          <View style={styles.webLayout}>
            <View style={styles.leftSection}>
              <Image
                source={require('../../assets/delivery-character.png')}
                style={styles.image}
              />
              <Text style={styles.leftTitle}>Bem-vindo!</Text>
              <Text style={styles.leftSubtitle}>Faça login e entre para o nosso time</Text>
            </View>
            <View style={styles.rightSection}>
              <View style={styles.loginBox}>
                <Text style={styles.loginTitle}>LOGIN</Text>
                <View style={styles.inputGroup}>
                  <MaterialIcons name="person" size={20} color="#00FF9D" style={styles.inputIcon} />
                  <TextInput
                    placeholder="Usuário"
                    placeholderTextColor="#aaaaaa"
                    style={styles.input}
                    value={email}
                    onChangeText={setEmail}
                  />
                </View>
                <View style={styles.inputGroup}>
                  <MaterialIcons name="lock" size={20} color="#00FF9D" style={styles.inputIcon} />
                  <TextInput
                    placeholder="Senha"
                    placeholderTextColor="#aaaaaa"
                    style={styles.input}
                    secureTextEntry={!showPassword}
                    value={password}
                    onChangeText={setPassword}
                  />
                  <TouchableOpacity onPress={() => setShowPassword(!showPassword)} style={styles.eyeIcon}>
                    <MaterialIcons
                      name={showPassword ? 'visibility' : 'visibility-off'}
                      size={20}
                      color="#00FF9D"
                    />
                  </TouchableOpacity>
                </View>
                <View style={styles.rememberMeContainer}>
                  <TouchableOpacity onPress={() => setRemember(!remember)} style={styles.checkbox}>
                    {remember && <MaterialIcons name="check" size={20} color="#00FF9D" />}
                  </TouchableOpacity>
                  <Text style={styles.rememberMeText}>Lembrar-me</Text>
                  <TouchableOpacity onPress={() => navigation.navigate('ForgotPasswordScreen')}>
                    <Text style={styles.forgotPasswordText}>Esqueci a senha</Text>
                  </TouchableOpacity>
                </View>
                <TouchableOpacity style={styles.loginButton} onPress={handleLogin}>
                  {loading ? (
                    <ActivityIndicator size="small" color="#000" />
                  ) : (
                    <Text style={styles.loginButtonText}>ENTRAR</Text>
                  )}
                </TouchableOpacity>
                <View style={styles.registerButtonsContainer}>
                  <TouchableOpacity
                    style={styles.iconButton}
                    onPress={() => navigation.navigate('RegisterMotoboy')}
                  >
                    <MaterialIcons name="motorcycle" size={30} color="#00FF9D" />
                    <Text style={styles.iconText}>Motoboy</Text>
                  </TouchableOpacity>
                  <View style={styles.registrationTextContainer}>
                    <MaterialIcons name="chevron-left" size={20} color="#00FF9D" />
                    <Text style={styles.registrationText}>Registro</Text>
                    <MaterialIcons name="chevron-right" size={20} color="#00FF9D" />
                  </View>
                  <TouchableOpacity
                    style={styles.iconButton}
                    onPress={() => navigation.navigate('RegisterCompany')}
                  >
                    <MaterialIcons name="business" size={30} color="#00FF9D" />
                    <Text style={styles.iconText}>Empresa</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.termsButton}
                    onPress={() => navigation.navigate('Termos')}
                  >
                    <Text style={styles.termsButtonText}>Ver Termos e Condições</Text>
                  </TouchableOpacity>

                </View>
              </View>
            </View>
          </View>
        ) : (
          <View style={styles.mobileLayout}>
            <Image
              source={require('../../assets/delivery-character.png')}
              style={styles.mobileImage}
            />
            <Text style={styles.mobileTitle}>Bem-vindo!</Text>
            <Text style={styles.mobileSubtitle}>Faça login e entre para o nosso time</Text>
            <View style={styles.loginBoxMobile}>
              <Text style={styles.loginTitle}>LOGIN</Text>
              <View style={styles.inputGroup}>
                <MaterialIcons name="person" size={20} color="#00FF9D" style={styles.inputIcon} />
                <TextInput
                  placeholder="Usuário"
                  placeholderTextColor="#aaaaaa"
                  style={styles.input}
                  value={email}
                  onChangeText={setEmail}
                />
              </View>
              <View style={styles.inputGroup}>
                <MaterialIcons name="lock" size={20} color="#00FF9D" style={styles.inputIcon} />
                <TextInput
                  placeholder="Senha"
                  placeholderTextColor="#aaaaaa"
                  style={styles.input}
                  secureTextEntry={!showPassword}
                  value={password}
                  onChangeText={setPassword}
                />
                <TouchableOpacity onPress={() => setShowPassword(!showPassword)} style={styles.eyeIcon}>
                  <MaterialIcons
                    name={showPassword ? 'visibility' : 'visibility-off'}
                    size={20}
                    color="#00FF9D"
                  />
                </TouchableOpacity>
              </View>
              <View style={styles.rememberMeContainer}>
                <TouchableOpacity onPress={() => setRemember(!remember)} style={styles.checkbox}>
                  {remember && <MaterialIcons name="check" size={20} color="#00FF9D" />}
                </TouchableOpacity>
                <Text style={styles.rememberMeText}>Lembrar-me</Text>
                <TouchableOpacity onPress={() => navigation.navigate('ForgotPasswordScreen')}>
                  <Text style={styles.forgotPasswordText}>Esqueci a senha</Text>
                </TouchableOpacity>
              </View>
              <TouchableOpacity style={styles.loginButton} onPress={handleLogin}>
                {loading ? (
                  <ActivityIndicator size="small" color="#000" />
                ) : (
                  <Text style={styles.loginButtonText}>ENTRAR</Text>
                )}
              </TouchableOpacity>
              <View style={styles.registerButtonsContainer}>
                <TouchableOpacity
                  style={styles.iconButton}
                  onPress={() => navigation.navigate('RegisterMotoboy')}
                >
                  <MaterialIcons name="motorcycle" size={30} color="#00FF9D" />
                  <Text style={styles.iconText}>Motoboy</Text>
                </TouchableOpacity>
                <View style={styles.registrationTextContainer}>
                  <MaterialIcons name="chevron-left" size={20} color="#00FF9D" />
                  <Text style={styles.registrationText}>Registro</Text>
                  <MaterialIcons name="chevron-right" size={20} color="#00FF9D" />
                </View>
                <TouchableOpacity
                  style={styles.iconButton}
                  onPress={() => navigation.navigate('RegisterCompany')}
                >
                  <MaterialIcons name="business" size={30} color="#00FF9D" />
                  <Text style={styles.iconText}>Empresa</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.termsButton}
                  onPress={() => navigation.navigate('Termos')}
                >
                  <Text style={styles.termsButtonText}>Ver Termos e Condições</Text>
                </TouchableOpacity>

              </View>
            </View>
          </View>
        )}
        <Text style={styles.versionText}>Versão 2.5</Text>
      </LinearGradient>
      <Modal visible={showPrivacyModal} animationType="slide" transparent={true}>
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Termos e Condições</Text>
            <ScrollView style={styles.privacyTextContainer}>
              <Text style={styles.privacyText}>
                Bem-vindo ao nosso aplicativo! Antes de prosseguir, você deve aceitar nossos Termos e Condições de Uso:
                {'\n\n'}1. Você concorda em usar este aplicativo de acordo com as leis aplicáveis.
                {'\n\n'}2. Não nos responsabilizamos por perdas causadas por uso inadequado do aplicativo.
                {'\n\n'}3. Seus dados serão tratados de acordo com nossa Política de Privacidade.
                {'\n\n'}4. Ao aceitar, você concorda em receber notificações sobre atualizações do aplicativo.
                {'\n\n'}Leia todos os detalhes em nossa página oficial.
              </Text>
            </ScrollView>
            <TouchableOpacity
              style={styles.acceptButton}
              onPress={acceptPrivacyTerms}
            >
              <Text style={styles.acceptButtonText}>Aceitar e Continuar</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
      <TouchableOpacity
  style={styles.deleteAccountButton}
  onPress={() => navigation.navigate('DeleteAccountScreen')}
>
  <Text style={styles.deleteAccountButtonText}>Excluir Conta</Text>
</TouchableOpacity>

    </>
  );
};
  
const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Layout Web
  webLayout: {
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 80,
  },
  leftSection: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  rightSection: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  // Layout Mobile
  mobileLayout: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  // Estilos Gerais
  image: {
    width: isLargeScreen ? 400 : 150, // Imagem maior para web
    height: isLargeScreen ? 400 : 150,
    resizeMode: 'contain',
    marginBottom: 20,
  },
  mobileImage: {
    width: 200,
    height: 200,
    resizeMode: 'contain',
    marginBottom: 10,
  },
  leftTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#00FF9D',
    marginBottom: 10,
  },
  leftSubtitle: {
    fontSize: 20,
    color: '#ffffff',
    textAlign: 'center',
  },
  mobileTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#00FF9D',
    marginBottom: 5,
    textAlign: 'center',
  },
  mobileSubtitle: {
    fontSize: 16,
    color: '#ffffff',
    textAlign: 'center',
    marginBottom: 15,
  },
  loginBox: {
    maxWidth: 500, // Limite máximo para web
    width: '90%',
    padding: 30,
    borderRadius: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.3,
    shadowRadius: 10,
  },
  loginBoxMobile: {
    width: '95%', // Mais largo no mobile
    paddingHorizontal: 20,
    paddingVertical: 30,
    borderRadius: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  loginTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#00FF9D',
    marginBottom: 20,
    textAlign: 'center',
  },
  inputGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    paddingHorizontal: 15,
    borderRadius: 5,
    marginBottom: 15,
  },
  inputIcon: {
    marginRight: 10,
  },
  input: {
    flex: 1,
    color: '#ffffff',
    fontSize: 16,
    paddingVertical: 10,
  },
  forgotPasswordText: {
    color: '#00FF9D',
    textAlign: 'right',
    marginBottom: 20,
    fontSize: 14,
  },
  loginButton: {
    backgroundColor: '#00FF9D',
    paddingVertical: 15,
    borderRadius: 5,
    alignItems: 'center',
    marginTop: 20,
  },
  loginButtonText: {
    color: '#000',
    fontWeight: 'bold',
    fontSize: 16,
  },
  registerButtonsContainer: {
    marginTop: 20,
    flexDirection: 'row',
    alignItems: 'center', // Alinha os itens verticalmente
    justifyContent: 'space-between',
  },
  registerButton: {
    backgroundColor: '#ffffff',
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 5,
    marginHorizontal: 5,
  },
  registerButtonText: {
    color: '#000',
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  iconButton: {
    alignItems: 'center',
    marginHorizontal: 10,
  },
  iconText: {
    color: '#00FF9D',
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: 5,
    textAlign: 'center',
  },
  registrationTextContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 10,
  },
  registrationText: {
    color: '#00FF9D',
    fontSize: 16,
    fontWeight: 'bold',
    marginHorizontal: 5, // Espaço entre as flechas e o texto
    textAlign: 'center',
  },
  rememberMeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#00FF9D',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
  rememberMeText: {
    color: '#00FF9D', // Verde
    fontSize: 14,
    fontWeight: 'bold', // Garante destaque no Android
    textAlign: 'center', // Centraliza em caso de layouts inconsistentes
  },
  forgotPasswordText: {
    color: '#00FF9D', // Verde, para combinar com o estilo
    fontSize: 14,
    marginLeft: 20, // Espaçamento entre "Lembrar-me" e "Esqueci a senha"
    textDecorationLine: 'underline', // Adiciona um sublinhado
  },
  versionText: {
    position: 'absolute',
    bottom: 10,
    right: 10,
    fontSize: 12,
    color: '#aaaaaa', // Cinza para não chamar muita atenção
    textAlign: 'right',
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
  },
  modalContent: {
    width: '90%',
    backgroundColor: '#ffffff',
    borderRadius: 10,
    padding: 20,
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#000',
  },
  privacyTextContainer: {
    maxHeight: 300,
    marginBottom: 15,
  },
  privacyText: {
    fontSize: 14,
    color: '#333',
    lineHeight: 20,
    textAlign: 'justify',
  },
  acceptButton: {
    backgroundColor: '#00FF9D',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 5,
  },
  acceptButtonText: {
    color: '#000',
    fontWeight: 'bold',
    fontSize: 16,
  },
  termsButton: {
    position: 'absolute',
    bottom: -75,
    left: 50,
    backgroundColor: '#00FF9D',
    paddingHorizontal: 15,
    paddingVertical: 10,
    borderRadius: 5,
  },  
  termsButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold',
  },
  deleteAccountButton: {
    position: 'absolute',
    bottom: 10,
    left: 10,
    backgroundColor: '#FF4D4D',
    paddingHorizontal: 10, // era 15
    paddingVertical: 6,    // era 10
    borderRadius: 4,       // levemente menor
    zIndex: 9999,
  },  
  deleteAccountButtonText: {
    color: '#fff',
    fontSize: 12,        // era 14
    fontWeight: 'bold',
  },  
});
export default LoginScreen;
